import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INSTALLMENT_INDICATORS,
  paymentCashAmount,
  WITHHOLDING_CODES,
  fromScaled,
  toScaled,
  withholdingFromRate,
  invoiceDueDatesDivergence,
  termsOverridden,
  validTerms,
  type CostSplitInput,
  type CostSplitIssue,
  type PaymentMethod,
  type StructuredTerms,
} from '@loot/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  api,
  ApiError,
  type InvoiceDetail,
  type PayableOrderLine,
  type LegalEntity,
  type PayableAging,
  type PayableDetail,
  type PayableFormOptions,
  type PayableInstallmentView,
  type PayableListItem,
  type PayableQueueInstallment,
  type PayableStatus,
  type InvoiceDeclared,
} from '../../api.ts';
import { changed, FormModal, FormSection } from '../../components/FormModal.tsx';
import { termsSummary } from '../../components/forms/PaymentTermsFields.tsx';
import { ListToolbar, SearchBox } from '../../components/ListFilters.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { Modal } from '../../components/Modal.tsx';
import { NetsuiteButton, NetsuiteReferencesSection } from '../../components/NetsuiteReferences.tsx';
import { CostCenterPicker, CostSplitEditor } from '../../components/CostCenterPicker.tsx';
import { TabPanel, Tabs } from '../../components/Tabs.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  NumCell,
  PageHeader,
  Select,
  Table,
  Textarea,
} from '../../components/ui.tsx';
import { CommentAction, partnerChoice } from '../partners/common.tsx';
import { useFormat } from '../purchasing/common.tsx';
import { CardFilterNotice, useCardFilter } from './card-filter.tsx';
import { InvoiceList } from './Invoices.tsx';
import { CurrencySelect } from '../../components/forms/CurrencySelect.tsx';

type Tone = 'neutral' | 'ok' | 'off' | 'info' | 'accent';
const STATUS_TONE: Record<PayableStatus, Tone> = {
  draft: 'neutral',
  in_approval: 'accent',
  approved: 'info',
  partially_paid: 'accent',
  paid: 'ok',
  cancelled: 'off',
  rejected: 'off',
};
const STATUSES: PayableStatus[] = [
  'draft',
  'in_approval',
  'approved',
  'partially_paid',
  'paid',
  'cancelled',
  'rejected',
];
const METHODS = ['bank_transfer', 'pix', 'boleto', 'card', 'other'] as const;
const TAX_CODES = WITHHOLDING_CODES;
type TaxRow = {
  taxCode: (typeof TAX_CODES)[number];
  baseAmount: string;
  rate: string;
  amount: string;
};

/** Soma em texto decimal, ignorando o que ainda não é número (nulo se nada valer). */
function sumAmounts(values: string[]): bigint | null {
  let total: bigint | null = null;
  for (const v of values) {
    try {
      total = (total ?? 0n) + toScaled(v);
    } catch {
      // Campo ainda vazio ou incompleto: fica fora da soma.
    }
  }
  return total;
}

/**
 * Retenções do título: um imposto por linha, com base, alíquota e valor. Com base e alíquota, o
 * valor sai da conta e pode ser corrigido; o sistema não decide quais impostos cabem (D-12).
 */
/** O que a nota declara e o título aproveitou: o que conferir antes de lançar. */
function DeclaredNotice({
  declared,
  orderLine,
  currency,
}: {
  declared: InvoiceDeclared;
  orderLine: boolean;
  currency: string;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const money = (v: string) => fmt.money(v, currency || 'BRL');
  const card = declared.paymentMeans === 'credit_card' || declared.paymentMeans === 'debit_card';
  return (
    <div className="flex flex-col gap-1 rounded-md border border-rule p-3 text-sm sm:col-span-2">
      <p className="font-medium">{t('finance.payablesPage.fromXml.title')}</p>
      {card && (
        <p role="alert" className="text-danger">
          {t(`finance.payablesPage.fromXml.${declared.paymentMeans}`)}
        </p>
      )}
      {declared.installments.length > 0 && (
        <p>
          {t('finance.payablesPage.fromXml.installments', {
            list: declared.installments
              .map((i) => `${fmt.date(i.dueDate)} (${money(i.amount)})`)
              .join(', '),
          })}
        </p>
      )}
      {orderLine && declared.withholdings.length > 0 && (
        <p>
          {t('finance.payablesPage.fromXml.orderWithholdings', {
            list: declared.withholdings
              .map((w) => `${t(`finance.payablesPage.taxCodes.${w.taxCode}`)} ${money(w.amount)}`)
              .join(', '),
          })}
        </p>
      )}
      <p className="text-muted">{t('finance.payablesPage.fromXml.check')}</p>
    </div>
  );
}

function WithholdingsEditor({
  rows,
  onChange,
  gross,
  currency,
  expectedNet = null,
}: {
  rows: TaxRow[];
  onChange: (rows: TaxRow[]) => void;
  gross: string;
  currency: string;
  /** Líquido que a nota declara: avisa quando as retenções não fecham com ele. */
  expectedNet?: string | null;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const marked = new Set(rows.map((r) => r.taxCode));
  const blank = (taxCode: TaxRow['taxCode']): TaxRow => ({
    taxCode,
    baseAmount: gross,
    rate: '',
    amount: '',
  });
  const toggle = (code: TaxRow['taxCode']) =>
    onChange(
      marked.has(code)
        ? rows.filter((r) => r.taxCode !== code)
        : TAX_CODES.filter((c) => marked.has(c) || c === code).map(
            (c) => rows.find((r) => r.taxCode === c) ?? blank(c),
          ),
    );
  const all = TAX_CODES.every((c) => marked.has(c));
  const update = (code: TaxRow['taxCode'], patch: Partial<TaxRow>) =>
    onChange(
      rows.map((r) => {
        if (r.taxCode !== code) return r;
        const next = { ...r, ...patch };
        // Base ou alíquota mudou: o valor acompanha a conta.
        if (!('amount' in patch)) {
          next.amount = withholdingFromRate(next.baseAmount, next.rate) ?? next.amount;
        }
        return next;
      }),
    );
  const withheld = sumAmounts(rows.map((r) => r.amount));
  const grossValue = sumAmounts([gross]);
  const expected = expectedNet ? sumAmounts([expectedNet]) : null;
  const net = grossValue === null ? null : grossValue - (withheld ?? 0n);
  const mismatch = expected !== null && net !== null && net !== expected;
  return (
    <fieldset className="flex flex-col gap-3 sm:col-span-2">
      <legend className="text-sm font-medium">
        {t('finance.payablesPage.fields.withholding')}
      </legend>
      <p className="text-sm text-muted">{t('finance.payablesPage.withholdingsHint')}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
        <label className="flex items-center gap-2 font-medium">
          <input
            type="checkbox"
            checked={all}
            onChange={() =>
              onChange(
                all ? [] : TAX_CODES.map((c) => rows.find((r) => r.taxCode === c) ?? blank(c)),
              )
            }
          />
          {t('finance.payablesPage.withholdingsAll')}
        </label>
        {TAX_CODES.map((c) => (
          <label key={c} className="flex items-center gap-2">
            <input type="checkbox" checked={marked.has(c)} onChange={() => toggle(c)} />
            {t(`finance.payablesPage.taxCodes.${c}`)}
          </label>
        ))}
      </div>
      {rows.map((r) => (
        <div key={r.taxCode} className="grid grid-cols-1 items-end gap-2 sm:grid-cols-4">
          <span className="text-sm font-medium sm:pb-2">
            {t(`finance.payablesPage.taxCodes.${r.taxCode}`)}
          </span>
          <Field label={t('finance.payablesPage.fields.base')}>
            <Input
              id={`pf-tax-${r.taxCode}-base`}
              inputMode="decimal"
              value={r.baseAmount}
              onChange={(e) => update(r.taxCode, { baseAmount: e.target.value })}
            />
          </Field>
          <Field label={t('finance.payablesPage.fields.rate')}>
            <Input
              id={`pf-tax-${r.taxCode}-rate`}
              inputMode="decimal"
              value={r.rate}
              onChange={(e) => update(r.taxCode, { rate: e.target.value })}
            />
          </Field>
          <Field label={t('finance.payablesPage.fields.taxAmount')} required>
            <Input
              id={`pf-tax-${r.taxCode}-amount`}
              inputMode="decimal"
              required
              value={r.amount}
              onChange={(e) => update(r.taxCode, { amount: e.target.value })}
            />
          </Field>
        </div>
      ))}
      {rows.length > 0 && withheld !== null && (
        <p className="text-sm">
          {t('finance.payablesPage.withholdingsTotal', {
            total: fmt.money(fromScaled(withheld), currency || 'BRL'),
            net:
              grossValue === null
                ? '—'
                : fmt.money(fromScaled(grossValue - withheld), currency || 'BRL'),
          })}
        </p>
      )}
      {mismatch && (
        <p role="alert" className="text-sm text-danger">
          {t('finance.payablesPage.fromXml.netMismatch', {
            declared: fmt.money(fromScaled(expected!), currency || 'BRL'),
            net: fmt.money(fromScaled(net!), currency || 'BRL'),
          })}
        </p>
      )}
    </fieldset>
  );
}
const BUCKETS = ['not_due', 'd1_30', 'd31_60', 'd61_90', 'over_90'] as const;
const TABS = ['list', 'invoices', 'aging'] as const;
const INDICATOR_LABEL = {
  overdue: 'finance.homeCards.overdueInstallments.title',
  scheduled_past: 'finance.homeCards.scheduledPast.title',
} as const;
const KEY = ['finance', 'payables'];
const today = () => new Date().toISOString().slice(0, 10);
const DOC_KIND = { nfe: 'NF-e', nfse: 'NFS-e', invoice: 'Invoice' } as const;
const EMPTY_SPLIT: CostSplitInput = {
  method: 'percent',
  lines: [
    { costCenterId: '', share: '' },
    { costCenterId: '', share: '' },
  ],
  payerCostCenterId: null,
};

function useAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      api.post(`/finance/payables${path}`, body ?? {}),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function PayablesPage() {
  const { t } = useTranslation();
  // A aba fica no endereço para os cartões da Visão geral abrirem a lista certa.
  const [tab, setTab] = useState<(typeof TABS)[number]>(() => {
    const wanted = new URLSearchParams(window.location.search).get('tab');
    return TABS.find((id) => id === wanted) ?? 'list';
  });
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('nav.payables')} description={t('finance.payablesPage.about')} />
      <Tabs
        idPrefix="payables"
        label={t('nav.payables')}
        value={tab}
        onChange={setTab}
        items={[
          { id: 'list', label: t('finance.payablesPage.tabs.list') },
          { id: 'invoices', label: t('finance.payablesPage.tabs.invoices') },
          { id: 'aging', label: t('finance.payablesPage.tabs.aging') },
        ]}
      />
      <TabPanel idPrefix="payables" id={tab}>
        {tab === 'list' ? <PayableList /> : tab === 'invoices' ? <InvoiceList /> : <Aging />}
      </TabPanel>
    </div>
  );
}

function PayableList() {
  const [indicator, clear] = useCardFilter(INSTALLMENT_INDICATORS);
  return indicator ? <InstallmentQueue indicator={indicator} onClear={clear} /> : <PayableTitles />;
}

/** Lista por parcela que os cartões da Visão geral abrem: cada linha é o que foi contado. */
function InstallmentQueue({
  indicator,
  onClear,
}: {
  indicator: (typeof INSTALLMENT_INDICATORS)[number];
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [openId, setOpenId] = useState<string | null>(null);
  const data = useQuery({
    queryKey: [...KEY, 'installments', indicator],
    queryFn: () =>
      api.get<{ installments: PayableQueueInstallment[] }>(
        `/finance/payables/installments?indicator=${indicator}`,
      ),
  });
  return (
    <div className="flex flex-col gap-4">
      <CardFilterNotice label={t(INDICATOR_LABEL[indicator])} onClear={onClear} />
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {data.isSuccess && (
        <Table
          head={[
            t('finance.payablesPage.fields.code'),
            t('finance.payablesPage.fields.legalEntity'),
            t('finance.payablesPage.fields.partner'),
            t('finance.homeCards.installment'),
            t('finance.payablesPage.fields.dueDate'),
            t('finance.payablesPage.fields.scheduledFor'),
            { label: t('finance.payablesPage.fields.open'), align: 'right' as const },
            '',
          ]}
        >
          {data.data.installments.length === 0 && (
            <EmptyRow colSpan={8}>{t('finance.homeCards.noInstallments')}</EmptyRow>
          )}
          {data.data.installments.map((r) => (
            <tr key={r.id}>
              <td className="font-mono text-sm">{r.code}</td>
              <td className="font-mono text-sm">{r.legalEntityCode ?? '—'}</td>
              <td>
                {r.partnerName ?? '—'}
                <div className="text-xs text-muted">{r.description}</div>
              </td>
              <td className="text-sm">{r.number}</td>
              <td className="text-sm">{fmt.date(r.dueDate)}</td>
              <td className="text-sm">{fmt.date(r.scheduledFor)}</td>
              <NumCell>{fmt.money(r.openAmount, r.currency)}</NumCell>
              <td className="text-right">
                <div className="flex justify-end gap-2">
                  <Button variant="secondary" onClick={() => setOpenId(r.payableId)}>
                    {t('finance.payablesPage.open')}
                  </Button>
                  <MasterDeleteButton table="payables.payable" id={r.payableId} compact />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
      {openId && <PayableModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function PayableTitles() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('open'),
  );
  const data = useQuery({
    queryKey: KEY,
    queryFn: () =>
      api.get<{ payables: PayableListItem[]; canCreate: boolean }>('/finance/payables'),
  });
  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const q = search.trim().toLowerCase();
  const rows = data.data.payables.filter(
    (r) =>
      (!status || r.status === status) &&
      (!q ||
        `${r.code} ${r.partnerName ?? ''} ${r.documentNumber ?? ''} ${r.netsuiteRecordNumber ?? ''} ${r.description}`
          .toLowerCase()
          .includes(q)),
  );
  return (
    <>
      <ListToolbar
        action={
          data.data.canCreate && (
            <Button onClick={() => setAdding(true)}>{t('finance.payablesPage.add')}</Button>
          )
        }
      >
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder={t('finance.payablesPage.search')}
        />
        <Select
          id="payables-status"
          aria-label={t('finance.payablesPage.fields.status')}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">{t('finance.payablesPage.allStatuses')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`finance.payablesPage.statuses.${s}`)}
            </option>
          ))}
        </Select>
      </ListToolbar>
      <Table
        head={[
          t('finance.payablesPage.fields.code'),
          t('finance.payablesPage.fields.legalEntity'),
          t('finance.payablesPage.fields.partner'),
          t('finance.payablesPage.fields.document'),
          { label: t('finance.payablesPage.fields.net'), align: 'right' as const },
          { label: t('finance.payablesPage.fields.open'), align: 'right' as const },
          t('finance.payablesPage.fields.nextDue'),
          t('finance.payablesPage.fields.status'),
          '',
        ]}
      >
        {rows.length === 0 && <EmptyRow colSpan={9}>{t('finance.payablesPage.empty')}</EmptyRow>}
        {rows.map((r) => (
          <tr key={r.id}>
            <td className="font-mono text-sm">{r.code}</td>
            <td className="font-mono text-sm">{r.legalEntityCode ?? '—'}</td>
            <td>
              {r.partnerName ?? '—'}
              <div className="text-xs text-muted">{r.description}</div>
            </td>
            <td className="font-mono text-xs">
              {r.documentNumber ?? '—'}
              {r.netsuiteRecordNumber && (
                <div className="text-muted">
                  {t('finance.payablesPage.netsuiteShort', { code: r.netsuiteRecordNumber })}
                </div>
              )}
            </td>
            <NumCell>{fmt.money(r.netAmount, r.currency)}</NumCell>
            <NumCell>{fmt.money(r.openAmount, r.currency)}</NumCell>
            <td className="text-sm">{fmt.date(r.nextDue)}</td>
            <td>
              <Badge tone={STATUS_TONE[r.status]}>
                {t(`finance.payablesPage.statuses.${r.status}`)}
              </Badge>
              {r.platformPaid && (
                <div className="mt-1">
                  <Badge tone="accent">{t('finance.payablesPage.platformPaid')}</Badge>
                </div>
              )}
            </td>
            <td className="text-right">
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setOpenId(r.id)}>
                  {t('finance.payablesPage.open')}
                </Button>
                <MasterDeleteButton table="payables.payable" id={r.id} compact />
              </div>
            </td>
          </tr>
        ))}
      </Table>
      {openId && <PayableModal id={openId} onClose={() => setOpenId(null)} />}
      {adding && <PayableForm onDone={() => setAdding(false)} />}
    </>
  );
}

function Aging() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [asOf, setAsOf] = useState(today());
  const data = useQuery({
    queryKey: [...KEY, 'aging', asOf],
    queryFn: () => api.get<PayableAging>(`/finance/payables/aging?asOf=${asOf}`),
  });
  return (
    <div className="flex flex-col gap-4">
      <ListToolbar>
        <Field label={t('finance.payablesPage.asOf')}>
          <Input
            id="aging-asof"
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </Field>
      </ListToolbar>
      {data.isPending ? (
        <Loading />
      ) : data.isError ? (
        <ErrorBox error={data.error} />
      ) : (
        <Table
          head={[
            t('finance.payablesPage.fields.legalEntity'),
            t('finance.payablesPage.fields.currency'),
            ...BUCKETS.map((b) => ({
              label: t(`finance.payablesPage.buckets.${b}`),
              align: 'right' as const,
            })),
            { label: t('finance.payablesPage.fields.total'), align: 'right' as const },
          ]}
        >
          {data.data.rows.length === 0 && (
            <EmptyRow colSpan={8}>{t('finance.payablesPage.agingEmpty')}</EmptyRow>
          )}
          {data.data.rows.map((r) => (
            <tr key={`${r.legalEntityId}-${r.currency}`}>
              <td className="font-mono text-sm">{r.legalEntityCode ?? '—'}</td>
              <td className="font-mono text-sm">{r.currency}</td>
              {BUCKETS.map((b) => (
                <NumCell key={b}>{fmt.money(r.buckets[b], r.currency)}</NumCell>
              ))}
              <NumCell>{fmt.money(r.total, r.currency)}</NumCell>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

const PROVISIONAL_TONE = {
  pending: 'info',
  confirmed: 'ok',
  cadastre_error: 'off',
} as const;

function PayableModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useAction();
  const data = useQuery({
    queryKey: [...KEY, id],
    queryFn: () => api.get<PayableDetail>(`/finance/payables/${id}`),
  });
  const d = data.data;
  const item = (label: string, value: React.ReactNode) => (
    <div>
      <dt className="text-muted">{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
  return (
    <Modal
      open
      size="xl"
      title={d ? `${d.code} · ${d.partnerName ?? ''}` : t('nav.payables')}
      description={d?.description}
      onClose={onClose}
      footer={
        d && (
          <>
            <MasterDeleteButton table="payables.payable" id={d.id} />
            {d.actions.cancel && (
              <CommentAction
                label={t('finance.payablesPage.cancel')}
                fieldLabel={t('finance.payablesPage.fields.reason')}
                variant="danger"
                pending={action.isPending}
                error={action.error}
                onConfirm={(reason) => action.mutate({ path: `/${d.id}/cancel`, body: { reason } })}
              />
            )}
            {d.actions.withdraw && (
              <Button
                variant="secondary"
                disabled={action.isPending}
                onClick={() => action.mutate({ path: `/${d.id}/withdraw` })}
              >
                {t('finance.payablesPage.withdraw')}
              </Button>
            )}
            {d.actions.submit && (
              <Button
                disabled={action.isPending}
                onClick={() => action.mutate({ path: `/${d.id}/submit` })}
              >
                {t('finance.payablesPage.submit')}
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>
              {t('finance.payablesPage.close')}
            </Button>
          </>
        )
      }
    >
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {d && (
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={STATUS_TONE[d.status]}>
              {t(`finance.payablesPage.statuses.${d.status}`)}
            </Badge>
            {d.cancelReason && <span className="text-sm text-muted">{d.cancelReason}</span>}
          </div>
          {d.provisionalReview && (
            <div className="flex flex-col gap-1 rounded-md border border-rule p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={PROVISIONAL_TONE[d.provisionalReview.status]}>
                  {t(`finance.provisionalReview.statuses.${d.provisionalReview.status}`)}
                </Badge>
                <span>
                  {t(`finance.provisionalReview.reasons.${d.allocationProvisionalReason}`)}
                </span>
              </div>
              <span className="text-muted">
                {t('finance.provisionalReview.manager', {
                  name: d.provisionalReview.managerName ?? '—',
                })}
              </span>
              {d.provisionalReview.status === 'pending' && d.provisionalReview.finalDeadline && (
                <span className="text-muted">
                  {t('finance.provisionalReview.deadlines', {
                    first: fmt.date(d.provisionalReview.firstDeadline),
                    final: fmt.date(d.provisionalReview.finalDeadline),
                  })}
                </span>
              )}
              {d.provisionalReview.supplierNoticeAt && (
                <span className="text-muted">{t('finance.provisionalReview.supplierNotice')}</span>
              )}
              {d.provisionalReview.answerNote && <span>{d.provisionalReview.answerNote}</span>}
            </div>
          )}
          <ErrorBox error={action.error} />
          {d.acceptanceIssue && (
            <p className="text-sm text-muted">
              {t(`finance.payablesPage.blockers.${d.acceptanceIssue}`)}
            </p>
          )}
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            {item(t('finance.payablesPage.fields.legalEntity'), d.legalEntityCode)}
            {item(
              t('finance.payablesPage.fields.document'),
              [d.documentType, d.documentSeries, d.documentNumber].filter(Boolean).join(' · '),
            )}
            {item(t('finance.payablesPage.fields.documentDate'), fmt.date(d.documentDate))}
            {item(t('finance.payablesPage.fields.competence'), d.competence)}
            {item(t('finance.payablesPage.fields.gross'), fmt.money(d.grossAmount, d.currency))}
            {item(
              t('finance.payablesPage.fields.withholding'),
              fmt.money(d.withholdingAmount, d.currency),
            )}
            {item(t('finance.payablesPage.fields.net'), fmt.money(d.netAmount, d.currency))}
            {item(
              t('finance.payablesPage.fields.method'),
              t(`finance.payablesPage.methods.${d.paymentMethod}`),
            )}
            {item(
              t('finance.payablesPage.fields.terms'),
              [
                d.terms.netDays != null &&
                  d.terms.installments != null &&
                  termsSummary(t, {
                    method: d.paymentMethod as PaymentMethod,
                    netDays: d.terms.netDays,
                    installments: d.terms.installments,
                    dueDay: d.terms.dueDay,
                  }),
                d.terms.source && t(`finance.payablesPage.termsSources.${d.terms.source}`),
                d.terms.originKind !== 'none' &&
                  t(`finance.payablesPage.termsOrigin.${d.terms.originKind}`),
              ]
                .filter(Boolean)
                .join(' · '),
            )}
            {d.terms.overrideReason &&
              item(t('finance.payablesPage.fields.overrideReason'), d.terms.overrideReason)}
            {item(t('finance.payablesPage.fields.createdBy'), d.createdByName)}
            {d.affectsCac != null &&
              item(
                t('finance.payablesPage.fields.affectsCac'),
                [
                  t(`finance.payablesPage.cac.${d.affectsCac ? 'yes' : 'no'}`),
                  d.affectsCacSource && t(`finance.payablesPage.cac.from.${d.affectsCacSource}`),
                ]
                  .filter(Boolean)
                  .join(' · '),
              )}
          </dl>
          {d.withholdings.length > 0 && (
            <Table
              head={[
                t('finance.payablesPage.fields.taxCode'),
                { label: t('finance.payablesPage.fields.base'), align: 'right' as const },
                { label: t('finance.payablesPage.fields.rate'), align: 'right' as const },
                { label: t('finance.payablesPage.fields.amount'), align: 'right' as const },
              ]}
            >
              {d.withholdings.map((w, i) => (
                <tr key={i}>
                  <td>{t(`finance.payablesPage.taxCodes.${w.taxCode}`)}</td>
                  <NumCell>{fmt.money(w.baseAmount, d.currency)}</NumCell>
                  <NumCell>{w.rate ?? '—'}</NumCell>
                  <NumCell>{fmt.money(w.amount, d.currency)}</NumCell>
                </tr>
              ))}
            </Table>
          )}
          {d.allocation.length > 0 && (
            <Table
              head={[
                t('finance.payablesPage.fields.beneficiary'),
                t('finance.payablesPage.fields.costCenterId'),
                { label: t('finance.payablesPage.fields.percent'), align: 'right' as const },
                { label: t('finance.payablesPage.fields.amount'), align: 'right' as const },
              ]}
            >
              {d.allocation.map((a) => (
                <tr key={a.costCenterId}>
                  <td>{a.legalEntityCode ?? '—'}</td>
                  <td>
                    {a.costCenterLabel ?? t('finance.payablesPage.allocationHidden')}
                    {a.isPayer && (
                      <span className="ml-2">
                        <Badge tone="neutral">{t('finance.payablesPage.payerArea')}</Badge>
                      </span>
                    )}
                  </td>
                  <NumCell>{a.percent}</NumCell>
                  <NumCell>{a.amount ? fmt.money(a.amount, d.currency) : '—'}</NumCell>
                </tr>
              ))}
            </Table>
          )}
          <PlatformOccurrences d={d} />
          <Installments d={d} />
          <NetsuiteReferencesSection entityType="finance_payable" entityId={d.id} />
        </div>
      )}
    </Modal>
  );
}

/** Compra de marketplace cobrada pela plataforma antes do aceite (C15): não libera o pagamento. */
function PlatformOccurrences({ d }: { d: PayableDetail }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [signaling, setSignaling] = useState(false);
  if (!d.canSignalPlatform && d.platformOccurrences.length === 0) return null;
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-base font-semibold">{t('finance.payablesPage.platform.title')}</h3>
      <p className="text-sm text-muted">{t('finance.payablesPage.platform.hint')}</p>
      {d.platformOccurrences.length > 0 && (
        <Table
          head={[
            t('finance.payablesPage.platform.paidOn'),
            t('finance.payablesPage.platform.reference'),
            { label: t('finance.payablesPage.fields.amount'), align: 'right' as const },
            t('finance.payablesPage.platform.signaledBy'),
          ]}
        >
          {d.platformOccurrences.map((o) => (
            <tr key={o.id}>
              <td className="text-sm">{fmt.date(o.paidOn)}</td>
              <td>
                {o.reference}
                {o.note && <div className="text-xs text-muted">{o.note}</div>}
              </td>
              <NumCell>{fmt.money(o.amount, o.currency)}</NumCell>
              <td className="text-sm">{o.createdByName ?? '—'}</td>
            </tr>
          ))}
        </Table>
      )}
      {d.canSignalPlatform && (
        <div>
          <Button variant="secondary" onClick={() => setSignaling(true)}>
            {t('finance.payablesPage.platform.signal')}
          </Button>
        </div>
      )}
      {signaling && <PlatformOccurrenceForm d={d} onDone={() => setSignaling(false)} />}
    </section>
  );
}

function PlatformOccurrenceForm({ d, onDone }: { d: PayableDetail; onDone: () => void }) {
  const { t } = useTranslation();
  const action = useAction();
  const [start] = useState(() => ({ paidOn: '', amount: '', reference: '', note: '' }));
  const [f, setF] = useState(start);
  return (
    <FormModal
      open
      size="md"
      title={t('finance.payablesPage.platform.signal')}
      dirty={changed(start, f)}
      saving={action.isPending}
      error={action.error}
      saveLabel={t('finance.payablesPage.platform.save')}
      onSave={() =>
        action.mutate(
          {
            path: `/${d.id}/platform-occurrences`,
            body: { ...f, amount: f.amount.replace(',', '.'), note: f.note || null },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('finance.payablesPage.platform.paidOn')} required>
          <Input
            id="plat-date"
            type="date"
            required
            value={f.paidOn}
            onChange={(e) => setF((s) => ({ ...s, paidOn: e.target.value }))}
          />
        </Field>
        <Field label={`${t('finance.payablesPage.fields.amount')} (${d.currency})`} required>
          <Input
            id="plat-amount"
            inputMode="decimal"
            required
            value={f.amount}
            onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))}
          />
        </Field>
        <Field label={t('finance.payablesPage.platform.reference')} required>
          <Input
            id="plat-reference"
            required
            maxLength={200}
            value={f.reference}
            onChange={(e) => setF((s) => ({ ...s, reference: e.target.value }))}
          />
        </Field>
        <Field label={t('finance.payablesPage.platform.note')}>
          <Input
            id="plat-note"
            maxLength={1000}
            value={f.note}
            onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function Installments({ d }: { d: PayableDetail }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [scheduling, setScheduling] = useState<PayableInstallmentView | null>(null);
  const [paying, setPaying] = useState<PayableInstallmentView | null>(null);
  const action = useAction();
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-base font-semibold">{t('finance.payablesPage.installments')}</h3>
      <Table
        head={[
          '#',
          t('finance.payablesPage.fields.dueDate'),
          { label: t('finance.payablesPage.fields.amount'), align: 'right' as const },
          { label: t('finance.payablesPage.fields.open'), align: 'right' as const },
          t('finance.payablesPage.fields.status'),
          '',
        ]}
      >
        {d.installments.map((i) => (
          <tr key={i.id}>
            <td>{i.number}</td>
            <td className="text-sm">
              {fmt.date(i.dueDate)}
              {i.scheduledFor && (
                <div className="text-xs text-muted">
                  {t('finance.payablesPage.scheduledFor', { date: fmt.date(i.scheduledFor) })}
                </div>
              )}
            </td>
            <NumCell>{fmt.money(i.amount, d.currency)}</NumCell>
            <NumCell>{fmt.money(i.openAmount, d.currency)}</NumCell>
            <td className="text-sm">{t(`finance.payablesPage.installmentStatuses.${i.status}`)}</td>
            <td className="text-right">
              <div className="flex flex-wrap justify-end gap-2">
                {i.canSchedule && (
                  <Button variant="secondary" onClick={() => setScheduling(i)}>
                    {t('finance.payablesPage.schedule')}
                  </Button>
                )}
                {i.canPay && (
                  <Button onClick={() => setPaying(i)}>{t('finance.payablesPage.pay')}</Button>
                )}
              </div>
              {i.payBlockedBySeparation && (
                <p className="text-xs text-muted">{t('finance.payablesPage.separation')}</p>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {d.installments
        .flatMap((i) => i.movements.map((m) => ({ i, m })))
        .map(({ i, m }) => (
          <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span>
              {t(`finance.payablesPage.movementKinds.${m.kind}`)} · #{i.number} ·{' '}
              {fmt.date(m.occurredOn)} · {m.amount ? fmt.money(m.amount, d.currency) : ''}{' '}
              {m.cashAmount && m.cashAmount !== m.amount && (
                <span className="text-muted">
                  (
                  {t('finance.payablesPage.paidCash', {
                    paid: fmt.money(m.cashAmount, d.currency),
                    interest: fmt.money(m.interestAmount, d.currency),
                    fine: fmt.money(m.fineAmount, d.currency),
                    discount: fmt.money(m.discountAmount, d.currency),
                  })}
                  ){' '}
                </span>
              )}
              {m.reversed && `(${t('finance.payablesPage.reversed')})`}
              <span className="block text-xs text-muted">
                {[
                  m.createdByName,
                  m.reference,
                  m.netsuiteRecordNumber &&
                    t('finance.payablesPage.netsuiteShort', { code: m.netsuiteRecordNumber }),
                  m.note,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
            {m.kind === 'payment' && (
              <NetsuiteButton entityType="finance_payment" entityId={m.id} />
            )}
            {m.canReverse && (
              <CommentAction
                label={t('finance.payablesPage.reverse')}
                fieldLabel={t('finance.payablesPage.fields.reason')}
                variant="danger"
                pending={action.isPending}
                error={action.error}
                onConfirm={(note) =>
                  action.mutate({ path: `/movements/${m.id}/reverse`, body: { note } })
                }
              />
            )}
          </div>
        ))}
      {scheduling && <ScheduleForm d={d} inst={scheduling} onDone={() => setScheduling(null)} />}
      {paying && <PaymentForm d={d} inst={paying} onDone={() => setPaying(null)} />}
    </section>
  );
}

function useOptions(legalEntityId: string) {
  return useQuery({
    queryKey: [...KEY, 'options', legalEntityId],
    queryFn: () =>
      api.get<PayableFormOptions>(`/finance/payables/form-options?legalEntityId=${legalEntityId}`),
    enabled: !!legalEntityId,
  });
}

function ScheduleForm({
  d,
  inst,
  onDone,
}: {
  d: PayableDetail;
  inst: PayableInstallmentView;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const action = useAction();
  const options = useOptions(d.legalEntityId);
  const [start] = useState(() => ({
    scheduledFor: inst.scheduledFor ?? inst.dueDate,
    treasuryAccountKey: inst.treasuryAccountKey ?? '',
  }));
  const [f, setF] = useState(start);
  return (
    <FormModal
      open
      size="md"
      title={t('finance.payablesPage.schedule')}
      dirty={changed(start, f)}
      saving={action.isPending}
      error={action.error}
      saveLabel={t('finance.payablesPage.schedule')}
      onSave={() =>
        action.mutate({ path: `/installments/${inst.id}/schedule`, body: f }, { onSuccess: onDone })
      }
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('finance.payablesPage.fields.scheduledFor')} required>
          <Input
            id="sch-date"
            type="date"
            required
            value={f.scheduledFor}
            onChange={(e) => setF((s) => ({ ...s, scheduledFor: e.target.value }))}
          />
        </Field>
        <Field label={t('finance.payablesPage.fields.treasuryAccount')} required>
          <Select
            id="sch-account"
            required
            value={f.treasuryAccountKey}
            onChange={(e) => setF((s) => ({ ...s, treasuryAccountKey: e.target.value }))}
          >
            <option value="">{t('finance.payablesPage.choose')}</option>
            {(options.data?.treasuryAccounts ?? []).map((a) => (
              <option key={a.accountKey} value={a.accountKey}>
                {a.nickname} · {a.currency} · {a.masked}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>
    </FormModal>
  );
}

function PaymentForm({
  d,
  inst,
  onDone,
}: {
  d: PayableDetail;
  inst: PayableInstallmentView;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useAction();
  const options = useOptions(d.legalEntityId);
  // Contas confirmadas do fornecedor (cadastro de Fornecedores), mascaradas: transferência e PIX
  // precisam de uma; boleto e cartão não.
  const accounts = useQuery({
    queryKey: [...KEY, d.id, 'partner-accounts'],
    queryFn: () =>
      api.get<
        {
          id: string;
          kind: string;
          bankCode: string | null;
          currency: string;
          holderName: string;
          masked: string;
          preferred: boolean;
        }[]
      >(`/finance/payables/${d.id}/partner-accounts`),
  });
  const preferred = accounts.data?.find((a) => a.preferred)?.id ?? '';
  const [start] = useState(() => ({
    amount: inst.openAmount,
    interestAmount: '',
    fineAmount: '',
    discountAmount: '',
    occurredOn: today(),
    treasuryAccountKey: inst.treasuryAccountKey ?? '',
    partnerBankAccountId: '',
    reference: '',
    netsuiteRecordNumber: '',
  }));
  const [f, setF] = useState(start);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const err = action.error instanceof ApiError ? action.error : null;
  const warnings =
    err?.code === 'payment_warnings' ? ((err.details.warnings as string[]) ?? []) : [];
  const blockers =
    err?.code === 'payment_blocked' ? ((err.details.blockers as string[]) ?? []) : [];
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  // Valor que sai do caixa, calculado à vista (o servidor e o banco conferem de novo).
  const decimal = /^\d{1,15}(\.\d{1,4})?$/;
  const cashOut =
    [f.amount, f.interestAmount, f.fineAmount, f.discountAmount].every(
      (v) => v === '' || decimal.test(v.trim()),
    ) && decimal.test(f.amount.trim())
      ? paymentCashAmount(f.amount.trim(), {
          interest: f.interestAmount.trim() || '0',
          fine: f.fineAmount.trim() || '0',
          discount: f.discountAmount.trim() || '0',
        })
      : null;
  return (
    <FormModal
      open
      size="md"
      title={t('finance.payablesPage.pay')}
      description={t('finance.payablesPage.payHint')}
      dirty={changed(start, f)}
      saving={action.isPending}
      error={warnings.length || blockers.length ? null : action.error}
      saveLabel={
        warnings.length ? t('finance.payablesPage.confirmWarnings') : t('finance.payablesPage.pay')
      }
      onSave={() =>
        action.mutate(
          {
            path: `/installments/${inst.id}/payments`,
            body: {
              ...f,
              interestAmount: f.interestAmount || '0',
              fineAmount: f.fineAmount || '0',
              discountAmount: f.discountAmount || '0',
              partnerBankAccountId: f.partnerBankAccountId || preferred || null,
              acknowledgeWarnings: [...acknowledged, ...warnings],
            },
          },
          {
            onSuccess: onDone,
            onError: () => setAcknowledged((a) => [...new Set([...a, ...warnings])]),
          },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        <Field
          label={t('finance.payablesPage.fields.amount')}
          hint={t('finance.payablesPage.fields.amountHint')}
          required
        >
          <Input id="pay-amount" required value={f.amount} onChange={set('amount')} />
        </Field>
        <Field label={t('finance.payablesPage.fields.interestAmount')}>
          <Input
            id="pay-interest"
            inputMode="decimal"
            value={f.interestAmount}
            onChange={set('interestAmount')}
          />
        </Field>
        <Field label={t('finance.payablesPage.fields.fineAmount')}>
          <Input
            id="pay-fine"
            inputMode="decimal"
            value={f.fineAmount}
            onChange={set('fineAmount')}
          />
        </Field>
        <Field label={t('finance.payablesPage.fields.discountAmount')}>
          <Input
            id="pay-discount"
            inputMode="decimal"
            value={f.discountAmount}
            onChange={set('discountAmount')}
          />
        </Field>
        <p className="text-sm sm:col-span-2">
          {t('finance.payablesPage.cashOut')}:{' '}
          <strong>{cashOut ? fmt.money(cashOut, d.currency) : '—'}</strong>
        </p>
        <Field label={t('finance.payablesPage.fields.paidOn')} required>
          <Input
            id="pay-date"
            type="date"
            required
            value={f.occurredOn}
            onChange={set('occurredOn')}
          />
        </Field>
        <Field label={t('finance.payablesPage.fields.treasuryAccount')} required>
          <Select
            id="pay-account"
            required
            value={f.treasuryAccountKey}
            onChange={set('treasuryAccountKey')}
          >
            <option value="">{t('finance.payablesPage.choose')}</option>
            {(options.data?.treasuryAccounts ?? []).map((a) => (
              <option key={a.accountKey} value={a.accountKey}>
                {a.nickname} · {a.currency} · {a.masked}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('finance.payablesPage.fields.partnerAccount')}>
          <Select
            id="pay-partner-account"
            value={f.partnerBankAccountId || preferred}
            onChange={set('partnerBankAccountId')}
          >
            <option value="">{t('finance.payablesPage.noPartnerAccount')}</option>
            {(accounts.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {[a.holderName, a.bankCode, a.masked, a.currency].filter(Boolean).join(' · ')}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('finance.payablesPage.fields.reference')}>
          <Input id="pay-reference" value={f.reference} onChange={set('reference')} />
        </Field>
        <Field label={t('netsuite.fields.record_number')} hint={t('netsuite.optional')}>
          <Input
            id="pay-netsuite"
            maxLength={100}
            value={f.netsuiteRecordNumber}
            onChange={set('netsuiteRecordNumber')}
          />
        </Field>
        {blockers.length > 0 && (
          <p className="rounded-md border border-danger p-2 text-sm text-danger sm:col-span-2">
            {blockers.map((b) => t(`finance.payablesPage.blockers.${b}`)).join(' · ')}
          </p>
        )}
        {warnings.length > 0 && (
          <p className="rounded-md border border-rule p-2 text-sm sm:col-span-2">
            {t('finance.payablesPage.warningsIntro')}{' '}
            {warnings.map((w) => t(`finance.payablesPage.warnings.${w}`)).join(' · ')}
          </p>
        )}
      </FormSection>
    </FormModal>
  );
}

/**
 * Nota de contrato de fornecedor com pedido aberto na empresa: pode haver sobreposição. Só avisa e
 * mostra os pedidos, sem afirmar que é a mesma despesa e sem impedir o título.
 */
function ContractOrdersNotice({ invoiceId }: { invoiceId: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const orders = useQuery({
    queryKey: [...KEY, 'open-orders', invoiceId],
    queryFn: () =>
      api.get<{
        orders: {
          id: string;
          number: string;
          requestId: string;
          currency: string;
          openAmount: string;
        }[];
      }>(`/finance/invoices/${invoiceId}/open-orders`),
  });
  if (!orders.data?.orders.length) return null;
  return (
    <div className="rounded-md border border-rule p-2 text-sm sm:col-span-2">
      <p>{t('finance.payablesPage.contractOrders')}</p>
      <ul className="mt-1">
        {orders.data.orders.map((o) => (
          <li key={o.id}>
            <a
              className="underline"
              href={`/purchasing/requests/${o.requestId}`}
              target="_blank"
              rel="noreferrer"
            >
              {o.number}
            </a>{' '}
            · {t('finance.payablesPage.contractOrdersOpen')} {fmt.money(o.openAmount, o.currency)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Lançar o título; vindo de uma nota de fornecedor, o documento e o valor vêm dela (ADR 0081, C9). */
export function PayableForm({
  onDone,
  invoice,
  orderLine,
}: {
  onDone: () => void;
  invoice?: InvoiceDetail;
  /** Nota de pedido (F1): o título nasce do item do pedido, com a classificação do item. */
  orderLine?: PayableOrderLine;
}) {
  const { t } = useTranslation();
  const action = useAction();
  const entities = useQuery({
    queryKey: ['core', 'legal-entities'],
    queryFn: () => api.get<LegalEntity[]>('/core/legal-entities'),
  });
  const fmt = useFormat();
  const declared = invoice?.fromXml ?? null;
  // Duplicatas da NF-e: o primeiro vencimento vira o prazo, contado da emissão.
  const firstDue = declared?.installments[0]?.dueDate;
  const dueDays =
    firstDue && invoice?.issueDate
      ? Math.max(0, Math.round((Date.parse(firstDue) - Date.parse(invoice.issueDate)) / 86_400_000))
      : null;
  const [start] = useState(() => ({
    legalEntityId: invoice?.legalEntityId ?? '',
    partnerId: invoice?.partnerId ?? '',
    documentType: invoice ? DOC_KIND[invoice.documentKind] : 'NFS-e',
    documentNumber: invoice?.documentNumber ?? '',
    documentSeries: invoice?.documentSeries ?? '',
    documentDate: invoice?.issueDate ?? today(),
    description: orderLine?.description ?? '',
    competence: declared?.competence ?? (invoice?.issueDate ?? today()).slice(0, 7),
    grossAmount: orderLine?.amount ?? invoice?.grossAmount ?? '',
    currency: invoice?.currency ?? '',
    affectsCac: '',
    expenseTypeId: orderLine?.expenseTypeId ?? '',
    classificationId: orderLine?.classificationId ?? '',
    costCenterId: orderLine?.costCenterId ?? '',
    paymentMethod: 'bank_transfer',
    netDays: dueDays !== null ? String(dueDays) : '30',
    installments: declared?.installments.length ? String(declared.installments.length) : '1',
    dueDay: '',
    baseDate: invoice?.issueDate ?? today(),
    overrideReason: '',
  }));
  const [f, setF] = useState(start);
  // Condição de pagamento (ADR 0104): a estruturada do pedido ou do contrato pré-preenche e vale
  // (D7); sem ela, o padrão do fornecedor é sugestão a confirmar (D6). Quem lança pode mudar,
  // escrevendo o motivo.
  const termsContractId = orderLine ? null : (invoice?.contractId ?? null);
  const sourceTerms = useQuery({
    queryKey: [
      ...KEY,
      'source-terms',
      f.legalEntityId,
      f.partnerId,
      orderLine?.orderItemId,
      termsContractId,
    ],
    queryFn: () =>
      api.get<{
        originKind: 'purchase_order' | 'contract' | 'none';
        originCode: string | null;
        source: 'origin_document' | 'partner_default' | null;
        terms: StructuredTerms | null;
        needsConfirmation: boolean;
      }>(
        `/finance/payables/sources/terms?legalEntityId=${f.legalEntityId}&partnerId=${f.partnerId}` +
          `&orderItemId=${orderLine?.orderItemId ?? ''}&contractId=${termsContractId ?? ''}`,
      ),
    enabled: !!f.legalEntityId && !!f.partnerId,
  });
  const src = sourceTerms.data;
  const originTerms = src?.source === 'origin_document' ? src.terms : null;
  // Pré-preenche com a origem; com o padrão do fornecedor, só se a nota não trouxe vencimentos.
  const prefill =
    originTerms ??
    (src?.source === 'partner_default' && !declared?.installments.length ? src.terms : null);
  const [termsEdited, setTermsEdited] = useState(false);
  const shown =
    !termsEdited && prefill
      ? {
          paymentMethod: prefill.method as string,
          netDays: String(prefill.netDays),
          installments: String(prefill.installments),
          dueDay: prefill.dueDay ? String(prefill.dueDay) : '',
        }
      : {
          paymentMethod: f.paymentMethod,
          netDays: f.netDays,
          installments: f.installments,
          dueDay: f.dueDay,
        };
  const applied: StructuredTerms = {
    method: shown.paymentMethod as PaymentMethod,
    netDays: Number(shown.netDays),
    installments: Number(shown.installments),
    dueDay: shown.dueDay ? Number(shown.dueDay) : null,
  };
  const overridden = termsOverridden({
    origin: originTerms,
    applied,
    baseDate: f.baseDate,
    issueDate: f.documentDate || null,
  });
  const xmlDivergence =
    declared && validTerms(applied)
      ? invoiceDueDatesDivergence(
          applied,
          f.baseDate,
          declared.installments.map((i) => i.dueDate),
        )
      : null;
  // Retenções declaradas na nota; no título de item do pedido, a pessoa informa no título certo.
  const [startTaxes] = useState<TaxRow[]>(() =>
    orderLine
      ? []
      : (declared?.withholdings ?? []).map((w) => ({
          taxCode: w.taxCode,
          baseAmount: w.baseAmount ?? '',
          rate: w.rate ?? '',
          amount: w.amount,
        })),
  );
  const [taxes, setTaxes] = useState<TaxRow[]>(startTaxes);
  // Rateio pela tela (FIN-08, ADR 0092): só no título avulso sem contrato (o de pedido e o de
  // contrato recebem o rateio da origem). O centro principal é a área pagadora escolhida no rateio.
  const canSplit = !orderLine && !invoice?.contractId;
  const [splitOn, setSplitOn] = useState(false);
  const [split, setSplit] = useState<CostSplitInput>(EMPTY_SPLIT);
  const mainCenter = splitOn ? (split.payerCostCenterId ?? '') : f.costCenterId;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => {
    const value = e.target.value;
    // Trocar a empresa limpa os centros: cada centro é de uma empresa só.
    if (k === 'legalEntityId' && value !== f.legalEntityId && !orderLine) setSplit(EMPTY_SPLIT);
    setF((s) => ({
      ...s,
      [k]: value,
      ...(k === 'legalEntityId' && value !== s.legalEntityId && !orderLine
        ? { costCenterId: '' }
        : {}),
    }));
  };
  const options = useOptions(f.legalEntityId);
  const o = options.data;
  const serverSplitIssues =
    action.error instanceof ApiError && action.error.code === 'cost_split_invalid'
      ? (action.error.issues as unknown as CostSplitIssue[])
      : undefined;
  const chosen = o?.classifications.find((c) => c.id === f.classificationId);
  // Só o servidor sabe se a regra do contrato da nota cai em centro de marketing na competência.
  const contractId = orderLine ? null : invoice?.contractId;
  const contractCac = useQuery({
    queryKey: [...KEY, 'contract-cac', contractId, f.competence],
    queryFn: () =>
      api.get<{ ask: boolean }>(
        `/finance/payables/sources/contract-cac?contractId=${contractId}&competence=${f.competence}`,
      ),
    enabled: !!contractId && !!f.competence,
  });
  // Título de pedido: só pergunta se a compra não tem resposta e a competência cai em marketing.
  const orderCac = useQuery({
    queryKey: [...KEY, 'order-cac', orderLine?.orderItemId, f.competence],
    queryFn: () =>
      api.get<{ ask: boolean }>(
        `/finance/payables/sources/order-cac?orderItemId=${orderLine!.orderItemId}&competence=${f.competence}`,
      ),
    enabled: !!orderLine && !!f.competence,
  });
  // "Essa compra afetará o CAC?" no avulso de centro de marketing e no contrato com regra que cai
  // nele (C12).
  const askCac = orderLine
    ? !!orderCac.data?.ask
    : !!o?.costCenters.find((c) => c.id === mainCenter)?.isMarketing || !!contractCac.data?.ask;
  const expenseTypes = (o?.expenseTypes ?? []).filter(
    (e) => !chosen || chosen.expenseTypeIds === null || chosen.expenseTypeIds.includes(e.id),
  );
  // Campos da condição: mostram a pré-preenchida até a pessoa mudar algum deles.
  const valueOf = (k: keyof typeof f) => (k in shown ? shown[k as keyof typeof shown] : f[k]);
  const onEdit = (k: keyof typeof f) =>
    k in shown
      ? (e: { target: { value: string } }) => {
          const value = e.target.value;
          setTermsEdited(true);
          setF((s) => ({ ...s, ...shown, [k]: value }));
        }
      : set(k);
  const text = (k: keyof typeof f, required = true, type = 'text') => (
    <Field label={t(`finance.payablesPage.fields.${k}`)} required={required}>
      <Input
        id={`pf-${k}`}
        type={type}
        required={required}
        value={valueOf(k)}
        onChange={onEdit(k)}
      />
    </Field>
  );
  const choice = (
    k: keyof typeof f,
    items: { id: string; label: string; search?: string }[],
    required = true,
  ) => (
    <Field label={t(`finance.payablesPage.fields.${k}`)} required={required}>
      <Select id={`pf-${k}`} required={required} value={valueOf(k)} onChange={onEdit(k)}>
        <option value="">{t('finance.payablesPage.choose')}</option>
        {items.map((i) => (
          <option key={i.id} value={i.id} data-search={i.search}>
            {i.label}
          </option>
        ))}
      </Select>
    </Field>
  );
  return (
    <FormModal
      open
      size="xl"
      title={t('finance.payablesPage.add')}
      description={t('finance.payablesPage.formHint')}
      dirty={
        changed(start, f) ||
        JSON.stringify(taxes) !== JSON.stringify(startTaxes) ||
        (splitOn && JSON.stringify(split) !== JSON.stringify(EMPTY_SPLIT))
      }
      saving={action.isPending}
      error={action.error}
      saveLabel={t('finance.payablesPage.save')}
      onSave={() =>
        action.mutate(
          {
            path: '',
            body: {
              ...(orderLine
                ? {
                    originKind: 'purchase_order',
                    orderItemId: orderLine.orderItemId,
                    billing: orderLine.billing,
                  }
                : { originKind: 'manual' }),
              legalEntityId: f.legalEntityId,
              partnerId: f.partnerId,
              currency: f.currency || null,
              documentType: f.documentType,
              documentNumber: f.documentNumber,
              documentSeries: f.documentSeries,
              documentDate: f.documentDate || null,
              description: f.description,
              competence: f.competence,
              grossAmount: f.grossAmount,
              withholdings: taxes.map((r) => ({
                taxCode: r.taxCode,
                baseAmount: r.baseAmount || null,
                rate: r.rate || null,
                amount: r.amount,
              })),
              expenseTypeId: f.expenseTypeId,
              classificationId: f.classificationId,
              costCenterId: mainCenter,
              ...(canSplit && splitOn
                ? {
                    split: {
                      ...split,
                      lines: split.lines.filter((l) => l.costCenterId),
                    },
                  }
                : {}),
              // Sem resposta fica vazio: nunca vira "Não".
              affectsCac: askCac && f.affectsCac ? f.affectsCac === 'yes' : null,
              paymentMethod: shown.paymentMethod,
              invoiceId: invoice?.id ?? null,
              // A origem da condição é decidida no servidor; o motivo só vai quando ela muda.
              terms: {
                source: 'manual',
                netDays: Number(shown.netDays),
                installments: Number(shown.installments),
                dueDay: shown.dueDay ? Number(shown.dueDay) : null,
                baseDate: f.baseDate,
                overrideReason: overridden ? f.overrideReason : null,
              },
            },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        {choice(
          'legalEntityId',
          (entities.data ?? [])
            .filter((e) => e.isActive)
            .map((e) => ({ id: e.id, label: `${e.code} · ${e.legalName}` })),
        )}
        {choice('partnerId', (o?.partners ?? []).map(partnerChoice))}
        {text('documentType')}
        {text('documentNumber')}
        {text('documentSeries', false)}
        {text('documentDate', false, 'date')}
        <Field
          label={t('finance.payablesPage.fields.description')}
          className="sm:col-span-2"
          required
        >
          <Textarea
            id="pf-description"
            required
            minLength={3}
            value={f.description}
            onChange={set('description')}
          />
        </Field>
        {text('competence', true, 'month')}
        {text('grossAmount')}
        <Field label={t('finance.payablesPage.fields.currency')}>
          <CurrencySelect
            id="ap-currency"
            required={false}
            value={f.currency}
            onChange={(currency) => setF((s) => ({ ...s, currency }))}
          />
        </Field>
        {invoice?.contractId && !orderLine && <ContractOrdersNotice invoiceId={invoice.id} />}
        {choice(
          'classificationId',
          (o?.classifications ?? []).map((c) => ({ id: c.id, label: c.name })),
        )}
        {choice(
          'expenseTypeId',
          expenseTypes.map((e) => ({ id: e.id, label: e.name })),
        )}
        {/* Centros pela regra corporativa de elegibilidade (ADR 0092): da empresa do título. */}
        {canSplit && (
          <label className="flex items-center gap-2 text-sm font-medium sm:col-span-2">
            <input
              id="pf-split"
              type="checkbox"
              checked={splitOn}
              disabled={!f.legalEntityId}
              onChange={(e) => setSplitOn(e.target.checked)}
            />
            {t('finance.payablesPage.splitToggle')}
          </label>
        )}
        {canSplit && splitOn && f.legalEntityId ? (
          <fieldset className="flex flex-col gap-2 sm:col-span-2">
            <legend className="text-sm font-medium">{t('finance.payablesPage.splitTitle')}</legend>
            <p className="text-sm text-muted">{t('finance.payablesPage.splitHint')}</p>
            <CostSplitEditor
              value={split}
              onChange={setSplit}
              legalEntityId={f.legalEntityId}
              allowCrossCompany={false}
              methods={['percent', 'amount']}
              total={f.grossAmount.trim() || null}
              currency={f.currency || null}
              issues={serverSplitIssues}
            />
          </fieldset>
        ) : (
          <div className="sm:col-span-2">
            {f.legalEntityId ? (
              <CostCenterPicker
                label={t('finance.payablesPage.fields.costCenterId')}
                value={f.costCenterId}
                onChange={(costCenterId) => setF((s) => ({ ...s, costCenterId }))}
                legalEntityId={f.legalEntityId}
                keepIds={orderLine?.costCenterId ? new Set([orderLine.costCenterId]) : undefined}
                required
              />
            ) : (
              <Field label={t('finance.payablesPage.fields.costCenterId')} required>
                <Select id="pf-costCenterId" disabled value="">
                  <option value="">{t('finance.payablesPage.chooseEntityFirst')}</option>
                </Select>
              </Field>
            )}
          </div>
        )}
        {askCac &&
          choice('affectsCac', [
            { id: 'yes', label: t('finance.payablesPage.cac.yes') },
            { id: 'no', label: t('finance.payablesPage.cac.no') },
          ])}
        {askCac && orderLine && (
          <p className="text-sm text-muted sm:col-span-2">
            {t('finance.payablesPage.cac.orderHint')}
          </p>
        )}
        {src && src.source && src.terms && (
          <p className="rounded-md bg-paper px-3 py-2 text-sm sm:col-span-2" role="note">
            {src.source === 'origin_document'
              ? t(`finance.payablesPage.termsFrom.${src.originKind}`, {
                  code: src.originCode ?? '—',
                  terms: termsSummary(t, src.terms),
                })
              : t('finance.payablesPage.termsFrom.partner_default', {
                  terms: termsSummary(t, src.terms),
                })}
          </p>
        )}
        {choice(
          'paymentMethod',
          METHODS.map((m) => ({ id: m, label: t(`finance.payablesPage.methods.${m}`) })),
        )}
        {text('netDays', true, 'number')}
        {text('installments', true, 'number')}
        {text('dueDay', false, 'number')}
        {text('baseDate', true, 'date')}
        {xmlDivergence && (
          <p className="rounded-md border border-rule px-3 py-2 text-sm sm:col-span-2" role="alert">
            {t('finance.payablesPage.termsXmlDivergence', {
              invoice: xmlDivergence.invoice.map((d) => fmt.date(d)).join(', '),
              calculated: xmlDivergence.calculated.map((d) => fmt.date(d)).join(', '),
            })}
          </p>
        )}
        {overridden && (
          <Field
            label={t('finance.payablesPage.fields.overrideReason')}
            hint={t('finance.payablesPage.overrideReasonHint')}
            className="sm:col-span-2"
            required
          >
            <Textarea
              id="pf-overrideReason"
              required
              minLength={3}
              maxLength={1000}
              value={f.overrideReason}
              onChange={set('overrideReason')}
            />
          </Field>
        )}
        {declared && (
          <DeclaredNotice declared={declared} orderLine={!!orderLine} currency={f.currency} />
        )}
        <WithholdingsEditor
          rows={taxes}
          onChange={setTaxes}
          gross={f.grossAmount}
          currency={f.currency}
          expectedNet={orderLine ? null : (declared?.netAmount ?? null)}
        />
      </FormSection>
    </FormModal>
  );
}
